<script setup>
import { ref, onMounted } from 'vue';

const videoRef = ref();
const showIcon = ref(false);
const isMuted = ref(true);
const isLoading = ref(true);

const toggleMute = () => {
  isMuted.value = !isMuted.value;
  videoRef.value.muted = isMuted.value;
};

onMounted(() => {
  //console.log(videoRef.value);
  videoRef.value.muted = isMuted.value;

  videoRef.value.addEventListener('canplaythrough', () => {
    isLoading.value = false;
  });

  videoRef.value.addEventListener('playing', () => {
    isLoading.value = false;
  });

  videoRef.value.addEventListener('waiting', () => {
    isLoading.value = true;
  });

  videoRef.value.addEventListener('timeupdate', () => {
    if (videoRef.value.currentTime >= 5) {
      showIcon.value = true;
    }
});
});
  
</script>

<template>
  <section id="AITSHero" class="relative flex flex-col items-center h-full mx-auto">
    <!-- headline + cta -->
    <div class="sm:10/12 2xl:8/12  w-11/12 flex flex-col justify-center items-center mx-auto gap-6 md:h-[100vh] h-[90vh] max-sm:px-6 pt-28">
      <h1 class="text-accent1 uppercase 2xl:text-6xl lg:text-5xl md:text-4xl sm:text-4xl text-[28px] text-center font-[700]">Innovating for a Smarter Future</h1>
      <p class="tracking-wide font-[200] max-sm:text-[14px] sm:w-2/3 text-justify text-accent2 w-full" style="text-align-last: center;">
        We combine our deep understanding of industrial challenges with our commitment to continuous innovation to deliver tailored software solutions that empower your business to thrive in an ever-evolving landscape. </p>
        <div class="relative video-container">
          <div :class="['loading-spinner', { hidden: !isLoading }]"></div>
          <video ref="videoRef" playsInline autoPlay muted loop preload="auto" width="100%" class="aits-video">
            <source preload="auto" src="/aits-video-min.mp4" type="video/mp4">
              Your browser does not support the video tag.
          </video>
          <button @click="toggleMute" class="mute-button">
            <img :src="isMuted ? '/images/unmute-icon-2.webp' : '/images/sound-icon.png'" alt="Mute/Unmute Icon" width="45px" height="43px"/>
          </button>
      </div>
        <RouterLink id="go-to-contact-page" aria-label="go to contact page" to="/contact" class="relative z-[4] block w-fit cursor-pointer px-4 py-3 max-sm:text-[14px] font-[200] text-center rounded-[8px] text-accent1 bg-bg2 hover:brightness-125 transition-all duration-400">Contact Our Experts</RouterLink>
     
    </div>
    <!-- animation elements -->
      <!-- middle layor -->
     <img src="/images/hero/elements/centermiddle.webp" alt="AITS" width="1253" height="1239" center cover responsive loading="eager"
      v-motion  
        :initial="{
          opacity: 1,
          transform: 'translate(-50%, 0%)',
          x:0,
          y:0,
        }"
        :visibleOnce="{
          opacity: 0,
          transform: 'translate(100%, -100%)',
          x:0,
          y:0,
          transition: {
            type: 'spring',
            delay: 800,
            duration: 600
          },
        }" class="absolute top-0 left-1/2 max-sm:top-1/4 max-sm:-translate-y-1/4 max-sm:h-[400px]" />
      
      <!-- left layer -->
      <img src="/images/hero/elements/lefttop_small.webp" alt="AITS" width="712" height="964" center cover responsive loading="eager"
      v-motion  
        :initial="{
          transform: 'translate(0%)',
          x:0,
          y:0,
        }"
        :visibleOnce="{
          transform: 'translateY(-70%)',
          x:0,
          y:0,
          transition: {
            type: 'spring',
            delay: 900,
            duration: 600
          },
        }" class="absolute top-0 left-0 max-sm:top-20 max-sm:h-[400px] overflow-hidden z-[-1]" />

      <img src="/images/hero/elements/leftmiddle_small.webp" alt="AITS" width="824" height="1096" center cover responsive loading="eager"
      class="absolute left-0 z-[-1] transform md:-translate-y-1/2 md:top-1/2 top-2/3 -translate-y-2/3  max-sm:h-[400px]"/>
      <img src="/images/hero/elements/leftmiddleoposite_small.webp" alt="AITS" width="962" height="1753" center cover responsive loading="eager"
      class="absolute left-0 z-[-1] transform md:-translate-y-1/2 md:top-1/2 top-2/3 -translate-y-2/3 max-sm:h-[400px]"/>    
      <img src="/images/hero/elements/leftbottom_small.webp" alt="AITS" width="616" height="1104" center cover responsive loading="eager"
      class="absolute left-0 top-[100%] z-[-1]"/>
 
      <!-- right layer -->
      <img src="/images/hero/elements/righttop_small.webp" alt="AITS" width="720" height="749" 
      class="min-h-screen w-screen absolute right-0 lg:top-1/4 lg:-translate-y-1/4 top-0 z-[-1]" center cover responsive loading="eager" />
      
      <img src="/images/hero/elements/rightmiddle_small.webp" alt="AITS" width="720" height="1151" center cover responsive loading="eager"
      v-motion  
        :initial="{
          transform: 'translate(0%)',
          x:0,
          y:0,
        }"
        :visibleOnce="{
          transform: 'translate(50%)',
          x:0,
          y:0,
          transition: {
            type: 'spring',
            delay: 100,
            duration: 600
          },
        }"
        class="absolute top-[35%] right-0 w-screen z-[-1]"/>    
      
      <!-- pattern -->
      <img src="/images/patterns/patternhero.webp" alt="AITS" width="626" height="1394" center cover responsive loading="eager"
      class="absolute z-[-1] right-0 top-1/3 transform -translate-y-1/3 opacity-75"/>

  </section>
</template>

<style scoped>
.video-container{width: 45%;}
video.aits-video{max-height: 380px;object-fit: cover;border-radius: 10px;}
.mute-button {
  position: absolute;
  bottom: 15px;
  right: 15px;
  border-radius: 50%;
  cursor: pointer;
 }

 .loading-spinner {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 40px;
    height: 40px;
    margin: -20px 0 0 -20px;
    border: 4px solid #f3f3f3;
    border-top: 4px solid #3498db;
    border-radius: 50%;
    animation: spin 1s linear infinite;
}

@keyframes spin {
    0% {
        transform: rotate(0deg);
    }
    100% {
        transform: rotate(360deg);
    }
}

@media (max-width: 1450px){
    .arrow-icon img{width: 145px;}
 }

 @media (max-width: 1024px){
    video.aits-video{height: unset;object-fit: contain;}
    .video-container{width: 65%;}
 }
@media (max-width: 767px){
  .mute-button img{
    width: 40px;
    height: 37px;
  }
  .arrow-icon img{width: 125px;}
  .video-container{width: 75%;}
}
@media (max-width: 627px){
  .video-container{width: 100%;}
}
</style>
